// StoriesStrip.tsx — Horizontal-scroll story rings for HomePage.
// Clicking any story card opens a visual-only modal overlay.

import { useState } from 'react';
import { Modal } from '@/components/primitives';
import { STORIES, type Story } from '@/lib/stories-mock';

const F = '"Inter Variable","Inter",system-ui,sans-serif';

function StoryRing({ story, onClick }: { story: Story; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      {/* Ring */}
      <div style={{
        width: 68,
        height: 68,
        borderRadius: '50%',
        padding: 3,
        background: story.live
          ? 'linear-gradient(135deg, #FF0050, #E8441A)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.06))',
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: '2px solid #0A0F1C',
          background: `url(${story.img}) center/cover`,
          position: 'relative',
          overflow: 'hidden',
        }}>
          <img
            src={story.img}
            alt={story.city}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          />
        </div>
      </div>
      {/* LIVE badge or username */}
      {story.live ? (
        <span style={{
          fontFamily: F,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.8,
          color: '#FF0050',
          textTransform: 'uppercase',
        }}>● LIVE</span>
      ) : (
        <span style={{
          fontFamily: F,
          fontSize: 11,
          fontWeight: 500,
          color: '#8C98B0',
          maxWidth: 64,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'center',
        }}>@{story.user}</span>
      )}
    </button>
  );
}

function StoryModal({ story, onClose }: { story: Story; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title="" width="380px">
      <div style={{ margin: -24, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ position: 'relative', height: 520 }}>
          <img
            src={story.img}
            alt={story.city}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 100%)',
          }}/>
          {/* Progress bar */}
          <div style={{
            position: 'absolute', top: 16, left: 16, right: 16,
            height: 3, background: 'rgba(255,255,255,0.3)', borderRadius: 2,
          }}>
            <div style={{ width: '60%', height: '100%', background: '#fff', borderRadius: 2 }}/>
          </div>
          {/* Author */}
          <div style={{
            position: 'absolute', top: 28, left: 16, right: 16,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: `url(${story.img}) center/cover`,
              border: '2px solid rgba(255,255,255,0.5)',
            }}/>
            <div>
              <div style={{ fontFamily: F, fontSize: 13, fontWeight: 700, color: '#fff' }}>@{story.user}</div>
              <div style={{ fontFamily: F, fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>
                {story.city}, {story.country}
              </div>
            </div>
            {story.live && (
              <span style={{
                marginLeft: 'auto', padding: '3px 8px', borderRadius: 999,
                background: '#FF0050', color: '#fff',
                fontFamily: F, fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
              }}>● LIVE</span>
            )}
          </div>
          {/* City */}
          <div style={{ position: 'absolute', bottom: 24, left: 20, color: '#fff' }}>
            <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2, opacity: 0.85, fontWeight: 600 }}>
              {story.country.toUpperCase()}
            </div>
            <h2 style={{
              fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif',
              fontSize: 44, fontWeight: 500, lineHeight: 0.95, margin: '4px 0 0',
              letterSpacing: -0.03,
            }}>{story.city}</h2>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function StoriesStrip() {
  const [activeStory, setActiveStory] = useState<Story | null>(null);

  return (
    <div style={{ padding: '32px 0 0' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        padding: '0 clamp(16px, 4vw, 64px) 16px',
      }}>
        <div>
          <div style={{
            fontFamily: F, fontSize: 11, letterSpacing: 2.5,
            color: '#8C98B0', fontWeight: 600,
          }}>FROM THE WORLD</div>
          <h2 style={{
            fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif',
            fontSize: 'clamp(28px,3vw,40px)', fontWeight: 500,
            color: '#F4F3F0', margin: '6px 0 0', letterSpacing: -0.02,
          }}>Live dispatches.</h2>
        </div>
        <span style={{ fontFamily: F, fontSize: 12, color: '#8C98B0', fontWeight: 600, letterSpacing: 0.5 }}>
          SEE ALL
        </span>
      </div>

      <div style={{
        display: 'flex', gap: 20, overflowX: 'auto', padding: '4px clamp(16px, 4vw, 64px) 8px',
        scrollbarWidth: 'none',
      }}>
        {STORIES.map(story => (
          <StoryRing
            key={story.id}
            story={story}
            onClick={() => setActiveStory(story)}
          />
        ))}
      </div>

      {activeStory && (
        <StoryModal story={activeStory} onClose={() => setActiveStory(null)} />
      )}
    </div>
  );
}
